export type FontFamilyKey = 'plus-jakarta' | 'inter' | 'system';
export type PageBackgroundKey = 'default' | 'warm' | 'cool' | 'slate';
export type SidebarType = 'full' | 'mini';

export interface AppearanceSettings {
  /** null = use theme default from CSS */
  accentHex: string | null;
  fontFamily: FontFamilyKey;
  pageBackground: PageBackgroundKey;
  sidebarType: SidebarType;
}

export const DEFAULT_APPEARANCE: AppearanceSettings = {
  accentHex: null,
  fontFamily: 'plus-jakarta',
  pageBackground: 'default',
  sidebarType: 'full',
};

export const ACCENT_PRESETS: { id: string; label: string; hex: string }[] = [
  { id: 'violet', label: 'Violet', hex: '#8B5CF6' },
  { id: 'indigo', label: 'Indigo', hex: '#6366F1' },
  { id: 'blue', label: 'Blue', hex: '#3B82F6' },
  { id: 'teal', label: 'Teal', hex: '#14B8A6' },
  { id: 'emerald', label: 'Emerald', hex: '#10B981' },
  { id: 'amber', label: 'Amber', hex: '#D97706' },
  { id: 'rose', label: 'Rose', hex: '#F43F5E' },
  { id: 'graphite', label: 'Graphite', hex: '#64748B' },
];

export const FONT_FAMILY_OPTIONS: {
  value: FontFamilyKey;
  label: string;
  cardLabel: string;
  sample: string;
}[] = [
  { value: 'plus-jakarta', label: 'Plus Jakarta Sans', cardLabel: 'Jakarta', sample: 'Aa' },
  { value: 'inter', label: 'Inter', cardLabel: 'Inter', sample: 'Aa' },
  { value: 'system', label: 'System UI', cardLabel: 'System', sample: 'Aa' },
];

const FONT_STACKS: Record<FontFamilyKey, string> = {
  'plus-jakarta': "'Plus Jakarta Sans', system-ui, -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif",
  inter: "'Inter', system-ui, -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif",
  system: "system-ui, -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif",
};

export function getFontFamilyStack(key: FontFamilyKey): string {
  return FONT_STACKS[key] ?? FONT_STACKS['plus-jakarta'];
}

const THEME_DEFAULT_ACCENT: Record<'light' | 'dark', string> = {
  dark: '#5B7FFF',
  light: '#3b82f6',
};

/** Resolved accent for UI when `accentHex` is null (theme default). */
export function getEffectiveAccentHex(
  accentHex: string | null,
  theme: 'light' | 'dark' = 'dark'
): string {
  if (accentHex) return accentHex;
  return THEME_DEFAULT_ACCENT[theme];
}

export const PAGE_BACKGROUND_OPTIONS: { value: PageBackgroundKey; label: string }[] = [
  { value: 'default', label: 'Default' },
  { value: 'warm', label: 'Warm' },
  { value: 'cool', label: 'Cool' },
  { value: 'slate', label: 'Slate' },
];

const PAGE_BG: Record<'light' | 'dark', Record<PageBackgroundKey, { bg: string | null; page: string | null }>> = {
  dark: {
    default: { bg: null, page: null },
    warm: { bg: '#12100e', page: '#0e0c0a' },
    cool: { bg: '#0f1218', page: '#0b0f14' },
    slate: { bg: '#0f1419', page: '#0b0d12' },
  },
  light: {
    default: { bg: null, page: null },
    warm: { bg: '#faf8f5', page: '#f5f2ed' },
    cool: { bg: '#f4f8fc', page: '#eef4fa' },
    slate: { bg: '#f1f5f9', page: '#e8edf3' },
  },
};

const ACCENT_CSS_VARS = [
  '--color-accent',
  '--color-nav-active',
  '--sidebar-accent',
] as const;

const ACCENT_DERIVED = [
  '--color-accent-hover',
  '--color-accent-light',
  '--sidebar-active-bg',
  '--sidebar-active-gradient',
] as const;

function normalizeHex(hex: string): string | null {
  const trimmed = hex.trim();
  if (!/^#?[0-9a-fA-F]{6}$/.test(trimmed.replace('#', ''))) return null;
  return trimmed.startsWith('#') ? trimmed : `#${trimmed}`;
}

function hexToRgb(hex: string): { r: number; g: number; b: number } | null {
  const normalized = normalizeHex(hex);
  if (!normalized) return null;
  const n = normalized.slice(1);
  return {
    r: parseInt(n.slice(0, 2), 16),
    g: parseInt(n.slice(2, 4), 16),
    b: parseInt(n.slice(4, 6), 16),
  };
}

function rgbToHex(r: number, g: number, b: number): string {
  const clamp = (v: number) => Math.max(0, Math.min(255, Math.round(v)));
  return `#${[clamp(r), clamp(g), clamp(b)]
    .map((c) => c.toString(16).padStart(2, '0'))
    .join('')}`;
}

function darkenHex(hex: string, amount: number): string {
  const rgb = hexToRgb(hex);
  if (!rgb) return hex;
  const f = 1 - amount;
  return rgbToHex(rgb.r * f, rgb.g * f, rgb.b * f);
}

/** HSL components for shadcn `hsl(var(--primary))` — "H S% L%" without wrapper */
export function hexToHslComponents(hex: string): string | null {
  const rgb = hexToRgb(hex);
  if (!rgb) return null;
  const r = rgb.r / 255;
  const g = rgb.g / 255;
  const b = rgb.b / 255;
  const max = Math.max(r, g, b);
  const min = Math.min(r, g, b);
  const l = (max + min) / 2;
  if (max === min) return `0 0% ${Math.round(l * 100)}%`;
  const d = max - min;
  const s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
  let h = 0;
  if (max === r) h = ((g - b) / d + (g < b ? 6 : 0)) / 6;
  else if (max === g) h = ((b - r) / d + 2) / 6;
  else h = ((r - g) / d + 4) / 6;
  return `${Math.round(h * 360)} ${Math.round(s * 100)}% ${Math.round(l * 100)}%`;
}

function setOrRemove(root: HTMLElement, name: string, value: string | null) {
  if (value == null) root.style.removeProperty(name);
  else root.style.setProperty(name, value);
}

export function applyAppearance(settings: AppearanceSettings, theme: 'light' | 'dark') {
  const root = document.documentElement;

  root.style.setProperty('--app-font', getFontFamilyStack(settings.fontFamily));
  root.dataset.sidebarType = settings.sidebarType;

  const bgPreset = PAGE_BG[theme][settings.pageBackground];
  setOrRemove(root, '--color-bg', bgPreset.bg);
  setOrRemove(root, '--sidebar-page-bg', bgPreset.page);

  if (!settings.accentHex) {
    for (const name of [...ACCENT_CSS_VARS, ...ACCENT_DERIVED]) {
      root.style.removeProperty(name);
    }
    root.style.removeProperty('--primary');
    root.style.removeProperty('--ring');
    return;
  }

  const accent = normalizeHex(settings.accentHex);
  if (!accent) return;

  const rgb = hexToRgb(accent);
  if (!rgb) return;

  const hover = darkenHex(accent, 0.12);
  const accentLight = `rgba(${rgb.r}, ${rgb.g}, ${rgb.b}, 0.15)`;
  const sidebarActive = `rgba(${rgb.r}, ${rgb.g}, ${rgb.b}, 0.12)`;

  for (const name of ACCENT_CSS_VARS) {
    root.style.setProperty(name, accent);
  }
  root.style.setProperty('--color-accent-hover', hover);
  root.style.setProperty('--color-accent-light', accentLight);
  root.style.setProperty('--sidebar-active-bg', sidebarActive);
  root.style.setProperty('--sidebar-active-gradient', sidebarActive);

  const hsl = hexToHslComponents(accent);
  if (hsl) {
    root.style.setProperty('--primary', hsl);
    root.style.setProperty('--ring', hsl);
  }
}
